import React, { useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Building2,
  Users,
  TrendingUp,
  Eye,
  ExternalLink,
  Home,
  CheckCircle,
  AlertCircle,
  X,
  Clock,
  Calculator,
  History as HistoryIcon,
  RefreshCw,
  Trash2,
  Car,
  Waves,
  Dumbbell,
  Bot,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Table,
  MapPin,
  Banknote,
  Radar,
  FileText,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/hooks/useAuth'
import { ConfirmationModal } from '@/components/monitoring/ConfirmationModal'
import { ProjectEditModal } from '@/components/ProjectEditModal'
import { UnitCreateModal } from '@/components/UnitCreateModal'
import { ProjectStatistics } from '@/components/ProjectStatistics'
import { ProjectHistory } from '@/components/ProjectHistory'
import { GoogleMapsViewer } from '@/components/GoogleMapsViewer'
import { DataWizard } from '@/components/automation/DataWizard'
import { ProjectReportModal } from '@/components/reports/ProjectReportModal'
import { isScrapableWebsite } from '@/lib/scrapableHosts'

// Utility function for consistent currency formatting
const formatCurrency = (amount: number) => {
  // Format number with Portuguese locale (space as thousands separator)
  const formattedNumber = new Intl.NumberFormat('pt-PT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)

  return `${formattedNumber} €`
}

// Utility function to calculate days on market
const calculateDaysOnMarket = (listingDate?: string, salesDate?: string): number => {
  if (!listingDate) return 0
  
  const listing = new Date(listingDate)
  const endDate = salesDate ? new Date(salesDate) : new Date()
  
  if (isNaN(listing.getTime())) return 0
  
  const diffTime = endDate.getTime() - listing.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  return Math.max(0, diffDays)
}

// Utility function to format parking display
const formatParking = (parking?: number): string => {
  if (!parking || parking === 0) return ''
  return `${parking} parking${parking > 1 ? 's' : ''}`
}

// Types
interface Project {
  project_id: number
  name: string
  website: string
  address?: string
  city?: string
  district?: string
  status?: string
  total_units: number
  sold_units: number
  reserved_units?: number
  avg_sqm_price: number
  totalValue?: number
  avgDaysToSell?: number
  last_scraped_at?: string
  created_at: string
  typology?: string
  full_description?: string
  area?: string
  neighbourhood?: string
  listing_date?: string
  has_parking?: boolean
  has_pool?: boolean
  has_gym?: boolean
  sold_out?: boolean
  sold_out_date?: string
  radar?: boolean
  yield_project?: number
  yield_duration?: number
  architect_id?: number
  developer_id?: number
  broker_id?: number
  latitude?: number
  longitude?: number
  scrap_status?: string
  scrap_error_message?: string
  payment_terms?: {
    reservation?: string
    promissory?: string
    payment_count?: string
    payment_percentage?: string
    deed?: string
    comment?: string
  }
  architect?: { name: string; architect_id: number }
  developer?: { name: string; developer_id: number }
  broker?: { name: string; broker_id: number }
  units: Unit[]
}

interface Unit {
  unit_id: number
  project_id: number
  unit_ref: string
  unit_url?: string
  typology?: string
  floor?: string
  fraction?: string
  status: string
  available?: boolean
  private_gpa?: number
  extra_gpa?: number
  total_gpa?: number
  listing_price_1?: number
  listing_price_2?: number
  listing_price_3?: number
  current_price?: number
  parking?: number
  listing_date?: string
  sales_date?: string
  created_at: string
  updated_at?: string
  last_scraped_at?: string
  sqm_price?: number
  time_on_market?: number
  reserved_date?: string
  reservation_count?: number
}

type UnitStatusFilter = 'all' | 'available' | 'reserved' | 'sold'

type UnitSortKey =
  | 'unit_ref'
  | 'typology'
  | 'fraction'
  | 'total_gpa'
  | 'current_price'
  | 'status'
  | 'listing_date'
  | 'sales_date'
  | 'last_scraped_at'
  | 'reservation_count'
  | 'time_on_market'
  | 'reserved_date'
  | 'private_gpa'
  | 'sqm_price'

interface UnitSortConfig {
  key: UnitSortKey | ''
  direction: 'asc' | 'desc' | null
}

const UNIT_SORT_LABELS: Record<UnitSortKey, string> = {
  unit_ref: 'Unit Reference',
  typology: 'Typology',
  fraction: 'Fraction',
  total_gpa: 'Total Area',
  current_price: 'Listing Price',
  status: 'Status',
  listing_date: 'Launch Date',
  sales_date: 'Sales Date',
  last_scraped_at: 'Last Update',
  reservation_count: 'Reservations',
  time_on_market: 'Days on Market',
  reserved_date: 'Reserved Date',
  private_gpa: 'Private Area',
  sqm_price: 'Price per m²',
}

const getUnitSortableValue = (unit: Unit, key: UnitSortKey): number | string => {
  switch (key) {
    case 'unit_ref':
      return (unit.unit_ref || '').toLowerCase()
    case 'typology':
      return (unit.typology || '').toLowerCase()
    case 'fraction':
      return (unit.fraction || '').toLowerCase()
    case 'status':
      return (unit.status || '').toLowerCase()
    case 'total_gpa':
      return unit.total_gpa ?? 0
    case 'private_gpa':
      return unit.private_gpa ?? 0
    case 'current_price':
      return unit.current_price ?? 0
    case 'reservation_count':
      return unit.reservation_count ?? 0
    case 'time_on_market':
      if (typeof unit.time_on_market === 'number' && !Number.isNaN(unit.time_on_market)) {
        return unit.time_on_market
      }
      return calculateDaysOnMarket(unit.listing_date, unit.sales_date)
    case 'listing_date':
      return unit.listing_date ? new Date(unit.listing_date).getTime() : 0
    case 'sales_date':
      return unit.sales_date ? new Date(unit.sales_date).getTime() : 0
    case 'reserved_date':
      return unit.reserved_date ? new Date(unit.reserved_date).getTime() : 0
    case 'last_scraped_at':
      return unit.last_scraped_at ? new Date(unit.last_scraped_at).getTime() : 0
    case 'sqm_price':
      if (typeof unit.sqm_price === 'number' && !Number.isNaN(unit.sqm_price)) {
        return unit.sqm_price
      }
      if ((unit.current_price ?? 0) > 0 && (unit.private_gpa ?? 0) > 0) {
        return (unit.current_price ?? 0) / (unit.private_gpa ?? 1)
      }
      return 0
    default:
      return 0
  }
}

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { canMutate, isAdmin, canExport } = useAuth()
  const fromProjectsState = (location.state as { fromProjects?: { search?: string } } | undefined)?.fromProjects
  const [selectedTab, setSelectedTab] = useState<'overview' | 'units' | 'stats' | 'history'>('overview')
  const [editingProject, setEditingProject] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [selectedUnitForHistory, setSelectedUnitForHistory] = useState<number | null>(null)
  const [showKPIInfoModal, setShowKPIInfoModal] = useState(false)
  const [selectedKPIInfo, setSelectedKPIInfo] = useState<{
    title: string
    formula: string
    explanation: string
  } | null>(null)
  const [isScraping, setIsScraping] = useState(false)
  const [showUnitModal, setShowUnitModal] = useState(false)
  const [unitModalMode, setUnitModalMode] = useState<'create' | 'clone'>('create')
  const [unitCloneData, setUnitCloneData] = useState<any | null>(null)
  const [unitStatusFilter, setUnitStatusFilter] = useState<UnitStatusFilter>('all')
  const [unitSortConfig, setUnitSortConfig] = useState<UnitSortConfig>({ key: '', direction: null })
  const [showDataWizard, setShowDataWizard] = useState(false)
  const [markSoldUnit, setMarkSoldUnit] = useState<Unit | null>(null)
  const [markSoldDate, setMarkSoldDate] = useState('')
  const [reportOpen, setReportOpen] = useState(false)

  const { data: project, isLoading, error } = useQuery<Project>({
    queryKey: ['project', id],
    queryFn: () => api.get(`/projects/${id}`).then(res => res.data),
    enabled: !!id
  })

  // Fetch detailed project statistics for enhanced Quick Stats
  const { data: projectStats } = useQuery({
    queryKey: ['project-statistics', id],
    queryFn: () => api.get(`/projects/${id}/statistics`).then(res => res.data),
    enabled: !!id
  })

  const { data: units, isLoading: unitsLoading } = useQuery({
    queryKey: ['project-units', id],
    queryFn: () => api.get(`/projects/${id}/units`).then(res => res.data.units),
    enabled: !!id
  })

  const totalUnitsCount = units?.length ?? 0

  const statusCounts = useMemo(() => {
    const base: Record<'available' | 'reserved' | 'sold', number> = {
      available: 0,
      reserved: 0,
      sold: 0,
    }

    if (!Array.isArray(units)) {
      return base
    }

    for (const unit of units) {
      if (unit.status === 'available') {
        base.available += 1
      } else if (unit.status === 'reserved') {
        base.reserved += 1
      } else if (unit.status === 'sold') {
        base.sold += 1
      }
    }

    return base
  }, [units])

  const conversionRateValue = useMemo(() => {
    if (!project || project.total_units <= 0) return 0
    const sold = Number(project.sold_units ?? 0)
    const reserved = Number(project.reserved_units ?? statusCounts.reserved ?? 0)
    const total = Number(project.total_units)
    return Math.round(((sold + reserved) / total) * 100)
  }, [project, statusCounts.reserved])

  const processedUnits = useMemo(() => {
    if (!Array.isArray(units)) {
      return [] as Unit[]
    }

    let result = [...units]

    if (unitStatusFilter !== 'all') {
      result = result.filter(unit => unit.status === unitStatusFilter)
    }

    if (unitSortConfig.key && unitSortConfig.direction) {
      const { key, direction } = unitSortConfig

      result = [...result].sort((a, b) => {
        const aValue = getUnitSortableValue(a, key)
        const bValue = getUnitSortableValue(b, key)

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const opts = key === 'fraction' ? { numeric: true, sensitivity: 'base' as const } : undefined
          return direction === 'asc'
            ? aValue.localeCompare(bValue, undefined, opts)
            : bValue.localeCompare(aValue, undefined, opts)
        }

        const numericA = Number(aValue) || 0
        const numericB = Number(bValue) || 0

        if (numericA < numericB) {
          return direction === 'asc' ? -1 : 1
        }
        if (numericA > numericB) {
          return direction === 'asc' ? 1 : -1
        }
        return 0
      })
    }

    return result
  }, [units, unitStatusFilter, unitSortConfig])

  const statusFilterOptions: Array<{ label: string; value: UnitStatusFilter; count: number }> = [
    { label: 'All', value: 'all', count: totalUnitsCount },
    { label: 'Available', value: 'available', count: statusCounts.available },
    { label: 'Reserved', value: 'reserved', count: statusCounts.reserved },
    { label: 'Sold', value: 'sold', count: statusCounts.sold },
  ]

  const hasActiveStatusFilter = unitStatusFilter !== 'all'
  const hasActiveUnitSort = unitSortConfig.key !== '' && unitSortConfig.direction !== null

  const unitSortDescriptor = useMemo(() => {
    if (!unitSortConfig.key || !unitSortConfig.direction) {
      return ''
    }

    const label = UNIT_SORT_LABELS[unitSortConfig.key]
    const directionLabel = unitSortConfig.direction === 'asc' ? 'ascending' : 'descending'

    return `${label} (${directionLabel})`
  }, [unitSortConfig])

  const { data: unitHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['unit-history', selectedUnitForHistory],
    queryFn: () => api.get(`/units/${selectedUnitForHistory}/history`).then(res => res.data),
    enabled: !!selectedUnitForHistory
  })

  const queryClient = useQueryClient()


  const handleBackToProjects = () => {
    const canGoBack = typeof window !== 'undefined' && window.history.length > 1

    if (fromProjectsState) {
      if (canGoBack) {
        navigate(-1)
        return
      }

      navigate(`/projects${fromProjectsState.search ?? ''}`)
      return
    }

    if (canGoBack) {
      navigate(-1)
      return
    }

    navigate('/projects')
  }


  const scrapingMutation = useMutation({
    mutationFn: (projectId: number) => {
      console.log(`[FRONTEND] Triggering scraping for project ${projectId}`)
      return api.post(`/projects/${projectId}/scrap`).then(res => res.data)
    },
    onSuccess: (data) => {
      console.log(`[FRONTEND] Scraping success:`, data)
      
      // Dismiss loading toast
      toast.dismiss('scraping-loading')
      
      queryClient.invalidateQueries({ queryKey: ['project', id] })
      
      // Show success notification with async processing info
      toast.success(
        <div className="flex flex-col">
          <div className="font-semibold">✅ Scraping Request Submitted!</div>
          <div className="text-sm text-gray-600 mt-1">
            Your web scraping request has been queued and will be processed in the background. 
            This may take a few minutes to complete.
          </div>
          <div className="text-xs text-gray-500 mt-2">
            You'll be notified when the data is updated.
          </div>
        </div>,
        { duration: 6000 }
      )
      setIsScraping(false)
    },
    onError: (error: any) => {
      console.error(`[FRONTEND] Scraping error:`, error)
      console.error(`[FRONTEND] Error details:`, {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        message: error?.message
      })
      
      // Dismiss loading toast
      toast.dismiss('scraping-loading')
      
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to trigger scraping. Please try again.'
      
      toast.error(
        <div className="flex flex-col">
          <div className="font-semibold">❌ Scraping Failed</div>
          <div className="text-sm text-gray-600 mt-1">{errorMessage}</div>
        </div>,
        { duration: 5000 }
      )
      setIsScraping(false)
    }
  })

  const markAsSoldMutation = useMutation({
    mutationFn: ({ unitId, salesDate }: { unitId: number; salesDate: string }) => {
      return api.put(`/units/${unitId}`, {
        status: 'sold',
        available: false,
        sales_date: salesDate,
      }).then(res => res.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-units', id] })
      queryClient.invalidateQueries({ queryKey: ['project', id] })
      queryClient.invalidateQueries({ queryKey: ['project-statistics', id] })
      setMarkSoldUnit(null)
      setMarkSoldDate('')
      toast.success('Unit marked as sold successfully')
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message ||
                          error?.message ||
                          'Failed to mark unit as sold. Please try again.'
      toast.error(errorMessage)
    }
  })

  const openMarkSoldModal = (unit: Unit) => {
    setMarkSoldUnit(unit)
    setMarkSoldDate(new Date().toISOString().split('T')[0])
  }

  // Toggle radar mutation
  const toggleRadarMutation = useMutation({
    mutationFn: (next: boolean) =>
      api.patch(`/projects/${id}/radar`, { radar: next }).then(res => res.data),
    onSuccess: (_data, next) => {
      queryClient.invalidateQueries({ queryKey: ['project', id] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success(next ? 'Added to radar' : 'Removed from radar')
    },
    onError: () => {
      toast.error('Failed to update radar status')
    },
  })

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: (projectId: number) => {
      return api.delete(`/projects/${projectId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('Project deleted successfully')
      if (fromProjectsState?.search) {
        navigate(`/projects${fromProjectsState.search}`)
      } else {
        navigate('/projects')
      }
    },
    onError: (error: any) => {
      console.error('Error deleting project:', error)
      toast.error('Failed to delete project')
    }
  })

  const handleDeleteProject = () => {
    if (project && window.confirm(`Are you sure you want to delete "${project.name}"? This action cannot be undone and will also delete all associated units.`)) {
      deleteProjectMutation.mutate(project.project_id)
    }
  }

  const handleUnitClick = (unitId: number) => {
    navigate(`/units/${unitId}`)
  }

  const handleUnitSort = (key: UnitSortKey) => {
    setUnitSortConfig(prev => {
      if (prev.key === key) {
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' }
        }
        if (prev.direction === 'desc') {
          return { key: '', direction: null }
        }
        return { key, direction: 'asc' }
      }

      return { key, direction: 'asc' }
    })
  }

  const getUnitSortIcon = (key: UnitSortKey) => {
    if (unitSortConfig.key !== key || !unitSortConfig.direction) {
      return <ChevronsUpDown className="h-4 w-4 text-secondary-400" />
    }

    if (unitSortConfig.direction === 'asc') {
      return <ChevronUp className="h-4 w-4 text-primary-600" />
    }

    return <ChevronDown className="h-4 w-4 text-primary-600" />
  }

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false)
    setSelectedUnitForHistory(null)
  }

  const handleCloseKPIInfoModal = () => {
    setShowKPIInfoModal(false)
    setSelectedKPIInfo(null)
  }

  const handleTriggerScraping = async () => {
    if (!project || !id) return
    
    setIsScraping(true)
    
    // Show immediate loading notification
    toast.loading(
      <div className="flex flex-col">
        <div className="font-semibold">🔄 Initiating Scraping Request...</div>
        <div className="text-sm text-gray-600 mt-1">
          Please wait while we process your request.
        </div>
      </div>,
      { id: 'scraping-loading' }
    )
    
    try {
      await scrapingMutation.mutateAsync(parseInt(id))
    } catch (error) {
      // Error is handled in the mutation
    }
  }

  // Check if website is supported for scraping (shared list: lib/scrapableHosts.ts)
  const isWebsiteSupportedForScraping = (website: string) => isScrapableWebsite(website)


  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-secondary-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-secondary-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-16 w-16 text-danger-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-secondary-900 mb-2">Project not found</h3>
        <p className="text-secondary-500 mb-4">The project you're looking for doesn't exist or has been removed.</p>
        <button onClick={handleBackToProjects} className="btn-primary">
          Back to Projects
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full lg:w-auto">
          <button
            onClick={handleBackToProjects}
            className="btn-secondary self-start sm:self-auto flex items-center justify-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-secondary-900">{project.name || 'Unnamed Project'}</h1>
            <p className="text-secondary-600 flex items-center mt-1">
              <MapPin className="h-4 w-4 mr-1 opacity-70" />
              {project.city && project.district ? `${project.city}, ${project.district}` : 'Location not available'}
            </p>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
          {isAdmin && (
            <button
              onClick={() => toggleRadarMutation.mutate(!project.radar)}
              disabled={toggleRadarMutation.isPending}
              className={`btn-secondary flex items-center justify-center ${
                project.radar
                  ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
                  : ''
              }`}
              title={project.radar ? 'Remove from radar' : 'Add to radar'}
            >
              <Radar
                className={`h-4 w-4 mr-2 ${
                  toggleRadarMutation.isPending ? 'animate-pulse' : ''
                } ${project.radar ? 'fill-current' : ''}`}
              />
              {project.radar ? 'In Radar' : 'Add to Radar'}
            </button>
          )}

          <button
            onClick={() => {
              if (project.website) {
                let url = project.website;
                if (!url.startsWith('http://') && !url.startsWith('https://')) {
                  url = 'https://' + url;
                }
                window.open(url, '_blank', 'noopener,noreferrer');
              }
            }}
            className={`btn-secondary flex items-center justify-center ${
              !project.website ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={!project.website}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Website
          </button>

          {canMutate && project.website && isWebsiteSupportedForScraping(project.website) && (
            <button
              onClick={handleTriggerScraping}
              disabled={isScraping}
              className="btn-secondary text-primary-600 flex items-center justify-center border-primary-200 hover:border-primary-300 hover:bg-primary-50"
              title={isScraping ? "Processing scraping request..." : "Trigger web scraping for this project"}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isScraping ? 'animate-spin' : ''}`} />
              {isScraping ? 'Scraping...' : 'Scrap Now'}
            </button>
          )}

          {canMutate && (
            <>
              <button
                onClick={() => setShowDataWizard(true)}
                className="btn-secondary flex items-center justify-center"
                title="Import unit data manually for this project"
              >
                <Table className="h-4 w-4 mr-2" />
                Units
              </button>
            </>
          )}

          {canExport && (
            <button
              onClick={() => setReportOpen(true)}
              className="btn-secondary flex items-center justify-center"
              title="Generate a PDF report for this project"
            >
              <FileText className="h-4 w-4 mr-2" />
              Project Report
            </button>
          )}

          {canMutate && (
            <>
              <div className="h-8 w-px bg-secondary-200 hidden sm:block mx-1"></div>

              <button
                onClick={() => setEditingProject(true)}
                className="btn-primary flex items-center justify-center"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </button>

              <button
                onClick={handleDeleteProject}
                disabled={deleteProjectMutation.isPending}
                className="btn-danger flex items-center justify-center"
                title="Delete this project"
              >
                {deleteProjectMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: Eye },
            { id: 'units', label: 'Units', icon: Home },
            { id: 'stats', label: 'Statistics', icon: TrendingUp },
            // History is admin-only — audit data sits behind the same gate as /audit.
            ...(isAdmin ? [{ id: 'history', label: 'History', icon: HistoryIcon }] : []),
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id as any)}
              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                selectedTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {selectedTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Project Details */}
              <div className="lg:col-span-2 space-y-6">
                <div className="card">
                  <h3 className="text-lg font-semibold text-secondary-900 mb-4">Project Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Project Name</label>
                      <p className="text-secondary-900 font-medium">{project.name}</p>
                    </div>
                    <div>
                      <label className="form-label">Website</label>
                      {project.website ? (
                        <a href={project.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 flex items-center">
                          {project.website}
                          <ExternalLink className="h-4 w-4 ml-1" />
                        </a>
                      ) : (
                        <p className="text-secondary-500">No website available</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label">Address</label>
                      <p className="text-secondary-900">{project.address || 'No address available'}</p>
                    </div>
                    <div>
                      <label className="form-label">Location</label>
                      <p className="text-secondary-900">{project.city && project.district ? `${project.city}, ${project.district}` : 'Location not available'}</p>
                    </div>
                    <div>
                      <label className="form-label">Status</label>
                      <span className={`status-badge ${
                        project.status === 'active' ? 'badge-success' :
                        project.status === 'sold_out' ? 'badge-info' :
                        project.status === 'pending' ? 'badge-warning' : 'badge-gray'
                      }`}>
                        {project.status ? project.status.replace('_', ' ') : 'Unknown'}
                      </span>
                    </div>
                    <div>
                      <label className="form-label">Launch Date</label>
                      <p className="text-secondary-900 font-medium">{project.listing_date ? new Date(project.listing_date).toLocaleDateString() : 'Not set'}</p>
                    </div>
                    <div>
                      <label className="form-label">Sales Date</label>
                      <p className="text-secondary-900 font-medium">{project.sold_out_date ? new Date(project.sold_out_date).toLocaleDateString() : 'Not set'}</p>
                    </div>
                  </div>
                </div>

                {/* Description Section */}
                {project.full_description && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-secondary-900 mb-4">Project Description</h3>
                    <p className="text-secondary-700 leading-relaxed whitespace-pre-wrap">{project.full_description}</p>
                  </div>
                )}

                {/* Associated Entities */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-secondary-900 mb-4">Associated Entities</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-secondary-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Building2 className="h-5 w-5 text-primary-600 mr-2" />
                        <span className="font-medium text-secondary-900">Architect</span>
                      </div>
                      {project.architect ? (
                        <button
                          onClick={() => navigate(`/architects/${project.architect.architect_id}`)}
                          className="text-primary-600 hover:text-primary-700 text-left hover:underline transition-colors"
                        >
                          {project.architect.name}
                        </button>
                      ) : (
                        <p className="text-secondary-700">Not assigned</p>
                      )}
                    </div>
                    <div className="p-4 bg-secondary-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Users className="h-5 w-5 text-primary-600 mr-2" />
                        <span className="font-medium text-secondary-900">Developer</span>
                      </div>
                      {project.developer ? (
                        <button
                          onClick={() => navigate(`/developers/${project.developer.developer_id}`)}
                          className="text-primary-600 hover:text-primary-700 text-left hover:underline transition-colors"
                        >
                          {project.developer.name}
                        </button>
                      ) : (
                        <p className="text-secondary-700">Not assigned</p>
                      )}
                    </div>
                    <div className="p-4 bg-secondary-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Building2 className="h-5 w-5 text-primary-600 mr-2" />
                        <span className="font-medium text-secondary-900">Broker</span>
                      </div>
                      {project.broker ? (
                        <button
                          onClick={() => navigate(`/brokers/${project.broker.broker_id}`)}
                          className="text-primary-600 hover:text-primary-700 text-left hover:underline transition-colors"
                        >
                          {project.broker.name}
                        </button>
                      ) : (
                        <p className="text-secondary-700">Not assigned</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Terms Section */}
                {project.payment_terms && Object.values(project.payment_terms).some(v => v) && (
                  <div className="card">
                    <div className="flex items-center space-x-2 mb-4">
                      <Banknote className="h-5 w-5 text-primary-600" />
                      <h3 className="text-lg font-semibold text-secondary-900">Payment Terms</h3>
                    </div>
                    <div className="divide-y divide-secondary-100">
                      {project.payment_terms.reservation && (
                        <div className="flex justify-between py-2">
                          <span className="text-secondary-600">Reservation</span>
                          <span className="font-medium text-secondary-900">{project.payment_terms.reservation}</span>
                        </div>
                      )}
                      {project.payment_terms.promissory && (
                        <div className="flex justify-between py-2">
                          <span className="text-secondary-600">Promissory</span>
                          <span className="font-medium text-secondary-900">{project.payment_terms.promissory}</span>
                        </div>
                      )}
                      {project.payment_terms.payment_count && (
                        <div className="flex justify-between py-2">
                          <span className="text-secondary-600">Nr. of Payments</span>
                          <span className="font-medium text-secondary-900">{project.payment_terms.payment_count}</span>
                        </div>
                      )}
                      {project.payment_terms.payment_percentage && (
                        <div className="flex justify-between py-2">
                          <span className="text-secondary-600">Percentage</span>
                          <span className="font-medium text-secondary-900">{project.payment_terms.payment_percentage}</span>
                        </div>
                      )}
                      {project.payment_terms.deed && (
                        <div className="flex justify-between py-2">
                          <span className="text-secondary-600">Deed</span>
                          <span className="font-medium text-secondary-900">{project.payment_terms.deed}</span>
                        </div>
                      )}
                      {project.payment_terms.comment && (
                        <div className="pt-3 mt-1 border-t border-secondary-200">
                          <span className="text-secondary-500 text-sm">Note: </span>
                          <span className="text-secondary-700 text-sm italic">{project.payment_terms.comment}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Scraper Status Section - Added at the end */}
                <div className="card">
                  <div className="flex items-center space-x-2 mb-4">
                    <Bot className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-secondary-900">Scraper Status</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="form-label">Last Scraped</label>
                      <p className="text-secondary-900">{project.last_scraped_at ? new Date(project.last_scraped_at).toLocaleDateString() : 'Never'}</p>
                    </div>
                    <div>
                      <label className="form-label">Scrap Status</label>
                      <div className="flex items-center space-x-2">
                        <span className={`status-badge ${
                          project.scrap_status === 'successful' ? 'badge-success' :
                          project.scrap_status === 'http_error' ? 'badge-danger' :
                          project.scrap_status === 'connection_error' ? 'badge-danger' :
                          project.scrap_status === 'other_error' ? 'badge-danger' :
                          project.scrap_status === 'pending' ? 'badge-warning' : 'badge-gray'
                        }`}>
                          {project.scrap_status ? project.scrap_status.replace('_', ' ') : 'Not set'}
                        </span>
                      </div>
                    </div>
                    {project.scrap_status && project.scrap_status !== 'successful' && project.scrap_error_message && (
                      <div>
                        <label className="form-label flex items-center space-x-2">
                          <AlertTriangle className="h-4 w-4 text-warning-600" />
                          <span>Error Message</span>
                        </label>
                        <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg">
                          <p className="text-warning-800 text-sm">{project.scrap_error_message}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Project Location and Stats */}
              <div className="space-y-6">
                {/* Project Location Map - Moved to top right */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-secondary-900 mb-4">Project Location</h3>
                  <GoogleMapsViewer 
                    project={project} 
                    height="300px"
                  />
                  
                  {/* Coordinates Section */}
                  {(project.latitude || project.longitude) && (
                    <div className="mt-4 pt-4 border-t border-secondary-200">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-secondary-500">Latitude</label>
                          <p className="text-sm text-secondary-900 font-medium">{project.latitude || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-xs text-secondary-500">Longitude</label>
                          <p className="text-sm text-secondary-900 font-medium">{project.longitude || 'Not set'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-secondary-900 mb-4">Quick Stats</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-secondary-600">Total Units</span>
                      <span className="font-semibold text-secondary-900">{project.total_units}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-secondary-600">Sold Units</span>
                      <span className="font-semibold text-secondary-900">{project.sold_units}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-secondary-600">Reserved Units</span>
                      <span className="font-semibold text-secondary-900">{project.reserved_units ?? statusCounts.reserved}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-secondary-600">Conversion Rate</span>
                      <span className="font-semibold text-secondary-900">{conversionRateValue}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-secondary-600">Avg Price/m²</span>
                      <span className="font-semibold text-secondary-900">{formatCurrency(Math.round(project.avg_sqm_price || 0))}</span>
                    </div>
                  </div>
                </div>

                {/* Enhanced Metrics */}
                {projectStats && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-secondary-900 mb-4">Detailed Metrics</h3>
                    <div className="space-y-4">
                      {/* GPA Metrics */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-secondary-700">GPA (m²)</h4>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="text-center p-2 bg-secondary-50 rounded">
                            <div className="text-secondary-500">Min</div>
                            <div className="font-semibold">{Math.round(projectStats.minGpa || 0)}</div>
                          </div>
                          <div className="text-center p-2 bg-primary-50 rounded">
                            <div className="text-primary-600">Avg</div>
                            <div className="font-semibold text-primary-900">{Math.round(projectStats.avgGpa || 0)}</div>
                          </div>
                          <div className="text-center p-2 bg-secondary-50 rounded">
                            <div className="text-secondary-500">Max</div>
                            <div className="font-semibold">{Math.round(projectStats.maxGpa || 0)}</div>
                          </div>
                        </div>
                      </div>

                      {/* Price/m² Metrics */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-secondary-700">Price/m² (€)</h4>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="text-center p-2 bg-secondary-50 rounded">
                            <div className="text-secondary-500">Min</div>
                            <div className="font-semibold">{formatCurrency(Math.round(projectStats.minSqmPrice || 0))}</div>
                          </div>
                          <div className="text-center p-2 bg-primary-50 rounded">
                            <div className="text-primary-600">Avg</div>
                            <div className="font-semibold text-primary-900">{formatCurrency(Math.round(projectStats.avgSqmPrice || 0))}</div>
                          </div>
                          <div className="text-center p-2 bg-secondary-50 rounded">
                            <div className="text-secondary-500">Max</div>
                            <div className="font-semibold">{formatCurrency(Math.round(projectStats.maxSqmPrice || 0))}</div>
                          </div>
                        </div>
                      </div>

                      {/* Time to Sell */}
                      <div className="flex items-center justify-between">
                        <span className="text-secondary-600">Avg Time to Sell</span>
                        <span className="font-semibold text-secondary-900">
                          {projectStats.avgMonthsToSell > 0 ? `${projectStats.avgMonthsToSell.toFixed(1)} months` : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>

            {/* Project Amenities - Moved below */}
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Project Amenities</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`flex items-center p-4 rounded-lg ${project.has_parking ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <Car className={`h-6 w-6 mr-3 ${project.has_parking ? 'text-green-600' : 'text-gray-400'}`} />
                  <div>
                    <div className={`font-medium ${project.has_parking ? 'text-green-900' : 'text-gray-500'}`}>
                      Parking
                    </div>
                    <div className={`text-sm ${project.has_parking ? 'text-green-700' : 'text-gray-400'}`}>
                      {project.has_parking ? 'Available' : 'Not Available'}
                    </div>
                  </div>
                </div>
                
                <div className={`flex items-center p-4 rounded-lg ${project.has_pool ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <Waves className={`h-6 w-6 mr-3 ${project.has_pool ? 'text-green-600' : 'text-gray-400'}`} />
                  <div>
                    <div className={`font-medium ${project.has_pool ? 'text-green-900' : 'text-gray-500'}`}>
                      Pool
                    </div>
                    <div className={`text-sm ${project.has_pool ? 'text-green-700' : 'text-gray-400'}`}>
                      {project.has_pool ? 'Available' : 'Not Available'}
                    </div>
                  </div>
                </div>
                
                <div className={`flex items-center p-4 rounded-lg ${project.has_gym ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <Dumbbell className={`h-6 w-6 mr-3 ${project.has_gym ? 'text-green-600' : 'text-gray-400'}`} />
                  <div>
                    <div className={`font-medium ${project.has_gym ? 'text-green-900' : 'text-gray-500'}`}>
                      Gym
                    </div>
                    <div className={`text-sm ${project.has_gym ? 'text-green-700' : 'text-gray-400'}`}>
                      {project.has_gym ? 'Available' : 'Not Available'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'units' && (
          <div className="card">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h3 className="text-lg font-semibold text-secondary-900">Project Units</h3>
                <p className="text-sm text-secondary-500">
                  {hasActiveStatusFilter ? `${processedUnits.length} of ${totalUnitsCount} units` : `${totalUnitsCount} units`}
                  {unitSortDescriptor && ` • Sorted by ${unitSortDescriptor}`}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {hasActiveUnitSort && (
                  <button
                    onClick={() => setUnitSortConfig({ key: '', direction: null })}
                    className="btn-secondary text-xs md:text-sm"
                  >
                    Reset Sorting
                  </button>
                )}
                {canMutate && (
                  <button
                    className="btn-primary"
                    onClick={() => {
                      setUnitModalMode('create')
                      setUnitCloneData(null)
                      setShowUnitModal(true)
                    }}
                  >
                    + Add Unit
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6">
              {statusFilterOptions.map(option => {
                const isActive = unitStatusFilter === option.value

                return (
                  <button
                    key={option.value}
                    onClick={() => setUnitStatusFilter(isActive && option.value !== 'all' ? 'all' : option.value)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                      isActive
                        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                        : 'bg-white text-secondary-600 border-secondary-200 hover:bg-secondary-50'
                    }`}
                  >
                    <span>{option.label}</span>
                    <span className={`text-xs ${isActive ? 'text-primary-100/90' : 'text-secondary-400'}`}>
                      {option.count}
                    </span>
                  </button>
                )
              })}
            </div>

            {unitsLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-16 bg-secondary-200 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : processedUnits.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200">
                  <thead className="bg-secondary-50 sticky top-0 z-10">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none group"
                        onClick={() => handleUnitSort('unit_ref')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Unit</span>
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                            {getUnitSortIcon('unit_ref')}
                          </span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none group"
                        onClick={() => handleUnitSort('typology')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Typology</span>
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                            {getUnitSortIcon('typology')}
                          </span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none group"
                        onClick={() => handleUnitSort('fraction')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Fraction</span>
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                            {getUnitSortIcon('fraction')}
                          </span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none group"
                        onClick={() => handleUnitSort('total_gpa')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Area (m²)</span>
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                            {getUnitSortIcon('total_gpa')}
                          </span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none group"
                        onClick={() => handleUnitSort('current_price')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Price</span>
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                            {getUnitSortIcon('current_price')}
                          </span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none group"
                        onClick={() => handleUnitSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                            {getUnitSortIcon('status')}
                          </span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none group"
                        onClick={() => handleUnitSort('time_on_market')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Days on Market</span>
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                            {getUnitSortIcon('time_on_market')}
                          </span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none group"
                        onClick={() => handleUnitSort('sales_date')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Sales Date</span>
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                            {getUnitSortIcon('sales_date')}
                          </span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none group"
                        onClick={() => handleUnitSort('last_scraped_at')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Last Update</span>
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                            {getUnitSortIcon('last_scraped_at')}
                          </span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none group"
                        onClick={() => handleUnitSort('reserved_date')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Reserved Date</span>
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                            {getUnitSortIcon('reserved_date')}
                          </span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none group"
                        onClick={() => handleUnitSort('reservation_count')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Reservations</span>
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                            {getUnitSortIcon('reservation_count')}
                          </span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {processedUnits.map((unit) => (
                      <tr 
                        key={unit.unit_id} 
                        className="hover:bg-secondary-50 cursor-pointer transition-colors"
                        onClick={() => handleUnitClick(unit.unit_id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-secondary-900">
                            {unit.unit_ref}
                          </div>
                          <div className="text-sm text-secondary-500">
                            Floor {unit.floor || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-secondary-900">
                            {unit.typology || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-secondary-900">
                            {unit.fraction || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-secondary-900">
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span>Private:</span>
                                <span>{Math.round(unit.private_gpa || 0)}m²</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span>Extra:</span>
                                <span>{Math.round(unit.extra_gpa || 0)}m²</span>
                              </div>
                              <div className="flex justify-between text-xs font-medium border-t pt-1">
                                <span>Total:</span>
                                <span>{Math.round(unit.total_gpa || 0)}m²</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-secondary-900">
                            {formatCurrency(unit.current_price || 0)}
                          </div>
                          <div className="text-sm text-secondary-500 flex items-center">
                            <Calculator className="h-3 w-3 mr-1" />
                            €{(() => {
                              const currentPrice = unit.current_price || 0;
                              const privateGpa = unit.private_gpa || 0;
                              const sqmPrice = unit.sqm_price;

                              if (sqmPrice && typeof sqmPrice === 'number' && sqmPrice > 0) {
                                return sqmPrice.toFixed(2);
                              } else if (currentPrice > 0 && privateGpa > 0) {
                                return (currentPrice / privateGpa).toFixed(2);
                              }
                              return 'N/A';
                            })()}/m²
                          </div>
                          {formatParking(unit.parking) && (
                            <div className="text-xs text-primary-600">
                              {formatParking(unit.parking)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`status-badge ${
                            unit.status === 'available' ? 'badge-success' :
                            unit.status === 'sold' ? 'badge-info' :
                            unit.status === 'reserved' ? 'badge-warning' : 'badge-gray'
                          }`}>
                            {unit.status}
                          </span>
                          {unit.available === false && (
                            <div className="text-xs text-secondary-500">Not Available</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-secondary-900 font-medium">
                            {calculateDaysOnMarket(unit.listing_date, unit.sales_date)} days
                          </div>
                          <div className="text-xs text-secondary-500">
                            Launched: {unit.listing_date ? new Date(String(unit.listing_date)).toLocaleDateString() : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-secondary-900">
                            {unit.sales_date ? new Date(String(unit.sales_date)).toLocaleDateString() : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-secondary-900">
                            {unit.last_scraped_at ? new Date(String(unit.last_scraped_at)).toLocaleDateString() : 'N/A'}
                          </div>
                          {unit.unit_url && (
                            <div className="mt-1">
                              <a
                                href={String(unit.unit_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-700 text-xs"
                                onClick={(e) => e.stopPropagation()}
                              >
                                View Listing →
                              </a>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-secondary-900">
                            {unit.reserved_date ? new Date(String(unit.reserved_date)).toLocaleDateString() : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-secondary-900">
                            {unit.reservation_count || 0} times
                          </div>
                        </td>
                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            {canMutate && unit.status !== 'sold' && (
                              <button
                                className="btn-primary text-sm flex items-center space-x-1"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  openMarkSoldModal(unit)
                                }}
                                disabled={markAsSoldMutation.isPending}
                              >
                                <CheckCircle className="h-4 w-4" />
                                <span>Mark as Sold</span>
                              </button>
                            )}
                            {canMutate && (
                              <button
                                className="btn-secondary text-sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setUnitModalMode('clone')
                                  setUnitCloneData({
                                    unit_ref: unit.unit_ref,
                                    unit_url: unit.unit_url,
                                    typology: unit.typology,
                                    floor: unit.floor,
                                    fraction: unit.fraction,
                                    status: unit.status,
                                    available: unit.available ?? true,
                                    private_gpa: unit.private_gpa,
                                    extra_gpa: unit.extra_gpa,
                                    total_gpa: unit.total_gpa,
                                    current_price: unit.current_price,
                                    parking: unit.parking,
                                  })
                                  setShowUnitModal(true)
                                }}
                              >
                                Clone
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <Home className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
                <p className="text-secondary-500">
                  {totalUnitsCount === 0
                    ? 'No units found for this project yet.'
                    : 'No units match the current filters. Try adjusting the status filter or reset sorting.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Unit Create / Clone Modal */}
        <UnitCreateModal
          isOpen={showUnitModal}
          onClose={() => setShowUnitModal(false)}
          projectId={Number(id)}
          initialData={unitCloneData ?? undefined}
          mode={unitModalMode}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['project-units', id] })
          }}
        />

        {selectedTab === 'stats' && (
          <ProjectStatistics projectId={Number(id)} />
        )}

        {selectedTab === 'history' && isAdmin && (
          <ProjectHistory projectId={Number(id)} />
        )}
      </div>

      {/* Unit History Modal */}
      {showHistoryModal && selectedUnitForHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-tagus-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900">
                Unit History - {units?.find(u => u.unit_id === selectedUnitForHistory)?.unit_ref}
              </h3>
              <button
                onClick={handleCloseHistoryModal}
                className="text-secondary-400 hover:text-secondary-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 max-h-[calc(90vh-140px)] overflow-y-auto">
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <span className="ml-3 text-secondary-600">Loading history...</span>
                </div>
              ) : unitHistory?.history && unitHistory.history.length > 0 ? (
                <div className="space-y-4">
                  {unitHistory.history.map((entry: any, index: number) => (
                    <div key={index} className="border border-secondary-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-secondary-500" />
                          <span className="text-sm font-medium text-secondary-900">
                            {new Date(entry.event_date).toLocaleString()}
                          </span>
                        </div>
                        <span className={`status-badge px-2 py-1 text-xs ${
                          entry.status === 'available' ? 'badge-success' :
                          entry.status === 'sold' ? 'badge-info' :
                          entry.status === 'reserved' ? 'badge-warning' : 'badge-gray'
                        }`}>
                          {entry.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-secondary-500">Price:</span>
                          <div className="font-medium">
                            {entry.price && typeof entry.price === 'number'
                              ? formatCurrency(entry.price)
                              : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span className="text-secondary-500">Price/m²:</span>
                          <div className="font-medium">
                            {entry.sqm_price && typeof entry.sqm_price === 'number'
                              ? `€${entry.sqm_price.toFixed(2)}`
                              : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span className="text-secondary-500">Available:</span>
                          <div className="font-medium">
                            {entry.available !== undefined ? (entry.available ? 'Yes' : 'No') : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span className="text-secondary-500">Status:</span>
                          <div className="font-medium">{entry.status || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {unitHistory.diffs && unitHistory.diffs.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-md font-semibold text-secondary-900 mb-3">Changes</h4>
                      <div className="space-y-3">
                        {unitHistory.diffs.map((diff: any, index: number) => (
                          <div key={index} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <div className="flex items-center mb-2">
                              <Clock className="h-4 w-4 text-amber-600 mr-2" />
                              <span className="text-sm font-medium text-amber-800">
                                {new Date(diff.event_date).toLocaleString()}
                              </span>
                            </div>
                            <div className="space-y-1">
                              {diff.changes.map((change: any, changeIndex: number) => (
                                <div key={changeIndex} className="text-xs text-amber-700">
                                  <span className="font-medium capitalize">{change.field}:</span>{' '}
                                  {change.from !== null && change.from !== undefined ? change.from : 'N/A'} →{' '}
                                  {change.to !== null && change.to !== undefined ? change.to : 'N/A'}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <HistoryIcon className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
                  <p className="text-secondary-500">No history available for this unit</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KPI Info Modal */}
      {showKPIInfoModal && selectedKPIInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-tagus-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                <Calculator className="h-5 w-5 mr-2 text-primary-600" />
                {selectedKPIInfo.title} - KPI Details
              </h3>
              <button
                onClick={handleCloseKPIInfoModal}
                className="text-secondary-400 hover:text-secondary-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="text-md font-semibold text-secondary-900 mb-2">Formula</h4>
                  <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3 font-mono text-sm text-secondary-800">
                    {selectedKPIInfo.formula}
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-semibold text-secondary-900 mb-2">Explanation</h4>
                  <div className="text-secondary-700 leading-relaxed">
                    {selectedKPIInfo.explanation}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCloseKPIInfoModal}
                  className="btn-primary"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      <ProjectEditModal
        isOpen={editingProject}
        onClose={() => setEditingProject(false)}
        projectId={parseInt(id!)}
        initialData={project ? {
          name: project.name || '',
          website: project.website || '',
          address: project.address || '',
          city: project.city || '',
          district: project.district || '',
          status: (project.status as 'active' | 'sold_out' | 'pending' | 'cancelled') || 'active',
          architect_id: project.architect_id ? project.architect_id : undefined,
          developer_id: project.developer_id ? project.developer_id : undefined,
          broker_id: project.broker_id ? project.broker_id : undefined,
          typology: project.typology || '',
          full_description: project.full_description || '',
          area: project.area || '',
          neighbourhood: project.neighbourhood || '',
          listing_date: project.listing_date ? new Date(project.listing_date).toISOString().split('T')[0] : '',
          has_parking: project.has_parking || false,
          has_pool: project.has_pool || false,
          has_gym: project.has_gym || false,
          sold_out: project.sold_out || false,
          sold_out_date: project.sold_out_date ? new Date(project.sold_out_date).toISOString().split('T')[0] : '',
          latitude: project.latitude,
          longitude: project.longitude,
          scrap_status: project.scrap_status as 'successful' | 'http_error' | 'connection_error' | 'other_error' | 'pending' | undefined,
          scrap_error_message: project.scrap_error_message || '',
        } : undefined}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['project', id] })
          setEditingProject(false)
        }}
      />
      <DataWizard
        isOpen={showDataWizard}
        onClose={() => setShowDataWizard(false)}
        initialProjectId={project.project_id}
      />

      {/* Mark as Sold Modal */}
      <ConfirmationModal
        isOpen={!!markSoldUnit}
        onClose={() => { setMarkSoldUnit(null); setMarkSoldDate('') }}
        onConfirm={() => {
          if (markSoldUnit && markSoldDate) {
            markAsSoldMutation.mutate({ unitId: markSoldUnit.unit_id, salesDate: markSoldDate })
          }
        }}
        title="Mark Unit as Sold"
        message={`Are you sure you want to mark unit ${markSoldUnit?.unit_ref || ''} as sold? This action cannot be easily undone.`}
        confirmLabel="Confirm Sale"
        confirmVariant="danger"
        isLoading={markAsSoldMutation.isPending}
      >
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">Sales Date</label>
          <input
            type="date"
            className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-white text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            value={markSoldDate}
            onChange={(e) => setMarkSoldDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
        </div>
      </ConfirmationModal>

      {reportOpen && project && (
        <ProjectReportModal
          projectId={Number(id)}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  )
}
